import { memo, useState, useCallback, useEffect, useMemo } from "react"
import { Button } from "@components/common/button"
import useTranslator from "@hooks/use-translator"
import {
  ListTodo,
  Clock,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  FolderOpen,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Ban,
  Download,
} from "lucide-react"
import {
  KpiCard,
  SkeletonKpiCard,
  ChartCard,
  StatusDonutChart,
  AssigneesBarChart,
  CompletionBar,
  StatusBadge,
  DrillDownSheet,
  DrillDownTable,
} from "../widgets"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@components/common/table"
import { cn, exportTableToExcel, getDrillDownSubtitle, getDrillDownTitle } from "@pma/utils/helpers"
import { useDrillDown } from "@hooks/use-drill-down"
import { useAuth } from "@pma/stores/auth-store"
import { useNavigate } from "react-router-dom"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { useAnalyticsMetadata, resolveEntityStatus } from "../analytics-engine"

import type { TasksAnalyticsProps, SortKey } from "./types"
import { useTasksQueries } from "./use-tasks-queries"
import { useTasksComputed } from "./use-tasks-computed"
import { SkeletonChart, SkeletonTable } from "./skeleton-loaders"
import { EmptyState } from "./empty-state"
import { TaskActivityChart } from "./task-activity-chart"
import { EmployeeBreakdownTable } from "./employee-breakdown-table"
import { TaskProgressTable } from "./task-progress-table"
import {
  KpiDrillDownContent,
  StatusDrillDownContent,
  ProjectDrillDownContent,
  AssigneeDrillDownContent,
  DateDrillDownContent,
} from "./drill-down"

export const TasksAnalytics = memo(function TasksAnalytics({
  filters,
  onCountLoaded,
}: TasksAnalyticsProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig, setTabManager } = useAuth()
  const orgId = authConfig?.last_organization_id
  const navigate = useNavigate()

  // Engine: fetches all dictionaries at runtime
  const engine = useAnalyticsMetadata()

  // Drill-down state
  const { state: drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  // Data queries
  const queries = useTasksQueries(orgId, filters)
  const {
    chartData,
    timeSeriesRaw,
    projectStatsRaw,
    projectsList,
    projectsWithTasksRaw,
    filteredTasks,
    fetchError,
    refetchAll,
    isLoading: queriesLoading,
  } = queries

  // Computed data
  const computed = useTasksComputed({
    filteredTasks,
    chartData,
    timeSeriesRaw,
    projectStatsRaw,
    projectsList,
    statuses: engine.statuses,
    filters,
    isHebrew,
  })

  const {
    totalTasks,
    statusData,
    kpiOpen,
    kpiCompleted,
    kpiCanceled,
    timeSeriesData,
    projectDonutData,
    totalProjects,
    taskCountsByProject,
    projectTableData,
    assigneeData,
    activeProjects,
    overdueProjects,
    atRiskProjects,
    avgCompletion,
  } = computed

  // Report count to parent for tab badge
  useEffect(() => {
    if (onCountLoaded && totalTasks > 0) onCountLoaded(totalTasks)
  }, [totalTasks, onCountLoaded])

  // Collapsible sections state
  const [projectsTableOpen, setProjectsTableOpen] = useState(true)

  // Sort state for projects table
  const [sortKey, setSortKey] = useState<SortKey>("completion")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return key
      }
      setSortDir("desc")
      return key
    })
  }, [])

  const navigateToProjectTasks = useCallback(
    (projectId: number) => {
      setTabManager({ group: "project-detail", main: "tasks", sub: "groups" })
      navigate(PROJECTS_PAGE_ROUTE + "/" + projectId)
    },
    [setTabManager, navigate],
  )

  const renderSortHeader = useCallback(
    (label: string, sortKeyName: SortKey) => (
      <button
        onClick={() => handleSort(sortKeyName)}
        className='flex items-center gap-1 text-xs font-semibold transition-colors hover:text-foreground'
      >
        {label}
        {sortKey === sortKeyName ? (
          sortDir === "asc" ? (
            <ChevronUp className='h-3 w-3' />
          ) : (
            <ChevronDown className='h-3 w-3' />
          )
        ) : (
          <ArrowUpDown className='h-3 w-3 opacity-40' />
        )}
      </button>
    ),
    [handleSort, sortKey, sortDir],
  )

  const sortedProjectTable = useMemo(() => {
    const sorted = [...projectTableData]
    sorted.sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case "title":
          cmp = a.title.localeCompare(b.title, isHebrew ? "he" : "en")
          break
        case "status":
          cmp = a.statusDisplay.name.localeCompare(b.statusDisplay.name, isHebrew ? "he" : "en")
          break
        case "totalTasks":
          cmp = a.totalTasks - b.totalTasks
          break
        case "doneTasks":
          cmp = a.doneTasks - b.doneTasks
          break
        case "completion":
          cmp = a.completion - b.completion
          break
      }
      return sortDir === "asc" ? cmp : -cmp
    })
    return sorted
  }, [projectTableData, sortKey, sortDir, isHebrew])

  const filteredProjectTable = useMemo(() => {
    if (!filters?.projectIds?.length) return sortedProjectTable
    return sortedProjectTable.filter((p) => filters.projectIds.includes(p.id))
  }, [sortedProjectTable, filters?.projectIds])

  // ---- Loading / Error / Empty states ----------------------------------------

  const isLoading = queriesLoading || engine.isLoading

  if (isLoading) {
    return (
      <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5'>
          {[...Array(5)].map((_, i) => <SkeletonKpiCard key={i} />)}
        </div>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {[...Array(4)].map((_, i) => <SkeletonKpiCard key={i} />)}
        </div>
        <SkeletonTable />
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <SkeletonChart />
          <SkeletonChart />
        </div>
        <SkeletonChart className='w-full' />
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-12' dir={isHebrew ? "rtl" : "ltr"}>
        <AlertTriangle className='h-10 w-10 text-red-500' />
        <p className='text-sm text-muted-foreground'>{fetchError}</p>
        <Button variant='outline' size='sm' onClick={refetchAll}>
          <RefreshCw className='mr-2 h-4 w-4' />
          {translate("Retry")}
        </Button>
      </div>
    )
  }

  const hasNoData =
    !chartData?.length && !timeSeriesRaw?.length && !projectStatsRaw?.length && !projectsList.length

  if (hasNoData) {
    return <EmptyState translate={translate} isHebrew={isHebrew} onRetry={refetchAll} />
  }

  // ---- Render ----------------------------------------------------------------
  return (
    <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
      {/* ============ SECTION 1: Project KPIs — 5 cards ============ */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5'>
        <KpiCard label={translate("total projects")} value={totalProjects} icon={FolderOpen} color='blue' />
        <KpiCard label={translate("active projects")} value={activeProjects} icon={ListTodo} color='green' />
        <KpiCard
          label={translate("overdue projects")}
          value={overdueProjects}
          icon={Clock}
          color='red'
          onClick={overdueProjects > 0 ? () => openDrillDown({ type: "overdue-projects" }) : undefined}
        />
        <KpiCard
          label={translate("at risk")}
          value={atRiskProjects}
          icon={AlertTriangle}
          color='amber'
          subtitle={translate("below 30% completion")}
          onClick={atRiskProjects > 0 ? () => openDrillDown({ type: "at-risk-projects" }) : undefined}
        />
        <KpiCard label={translate("average completion")} value={`${avgCompletion}%`} icon={TrendingUp} color='purple' />
      </div>

      {/* ============ SECTION 2: Task KPIs — 4 cards ============ */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        <KpiCard
          label={translate("My Tasks")}
          value={totalTasks}
          icon={ListTodo}
          color='blue'
          subtitle={translate("Tasks assigned to me")}
          onClick={() => openDrillDown({ type: "kpi", category: "all" })}
        />
        <KpiCard label={translate("Open Tasks")} value={kpiOpen} icon={Clock} color='amber' onClick={() => openDrillDown({ type: "kpi", category: "open" })} />
        <KpiCard label={translate("Completed Tasks")} value={kpiCompleted} icon={CheckCircle2} color='green' onClick={() => openDrillDown({ type: "kpi", category: "completed" })} />
        <KpiCard label={translate("Canceled Tasks")} value={kpiCanceled} icon={Ban} color='red' onClick={() => openDrillDown({ type: "kpi", category: "canceled" })} />
      </div>

      {/* ============ SECTION 3: Projects Overview Table ============ */}
      {filteredProjectTable.length > 0 && (
        <Card className='overflow-hidden border border-gray-100 shadow-sm transition-shadow duration-200 hover:shadow-md'>
          <CardHeader className='cursor-pointer select-none px-5 pb-3 pt-5' onClick={() => setProjectsTableOpen(!projectsTableOpen)}>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", !projectsTableOpen && "-rotate-90")} />
                <div>
                  <CardTitle className='text-sm font-semibold tracking-tight text-foreground'>{translate("All Tasks by Project")}</CardTitle>
                  <CardDescription className='text-xs'>
                    {translate("Org-wide task breakdown per project")} {" — "} {filteredProjectTable.length} {translate("projects")}
                  </CardDescription>
                </div>
              </div>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                title={translate("Export")}
                onClick={() => {
                  const rows = filteredProjectTable.map((p) => ({
                    [translate("project name")]: p.title,
                    [translate("status")]: p.statusDisplay.name,
                    [translate("tasks total")]: p.totalTasks,
                    [translate("tasks done")]: p.doneTasks,
                    [translate("completion")]: `${p.completion}%`,
                  }))
                  exportTableToExcel(rows, "tasks-by-project")
                }}
              >
                <Download className='h-4 w-4' />
              </Button>
            </div>
          </CardHeader>
          {projectsTableOpen && (
            <CardContent className='px-0 pb-3'>
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow className='hover:bg-transparent'>
                      <TableHead className='px-5 text-start'>{renderSortHeader(translate("project name"), "title")}</TableHead>
                      <TableHead className='text-start'>{renderSortHeader(translate("status"), "status")}</TableHead>
                      <TableHead className='text-center'>{renderSortHeader(translate("tasks total"), "totalTasks")}</TableHead>
                      <TableHead className='text-center'>{renderSortHeader(translate("tasks done"), "doneTasks")}</TableHead>
                      <TableHead className='min-w-[160px]'>{renderSortHeader(translate("completion"), "completion")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjectTable.map((project) => (
                      <TableRow
                        key={project.id}
                        className='cursor-pointer transition-colors hover:bg-gray-50/80'
                        onClick={() => openDrillDown({ type: "project", projectId: project.id, projectName: project.title })}
                      >
                        <TableCell
                          className='max-w-[200px] truncate px-5 text-sm font-medium text-primary hover:underline'
                          onClick={(e) => { e.stopPropagation(); navigateToProjectTasks(project.id) }}
                        >
                          {project.title}
                        </TableCell>
                        <TableCell><StatusBadge statusDisplay={project.statusDisplay} /></TableCell>
                        <TableCell className='text-center font-mono text-sm tabular-nums'>{project.totalTasks}</TableCell>
                        <TableCell className='text-center font-mono text-sm tabular-nums text-green-700'>{project.doneTasks}</TableCell>
                        <TableCell><CompletionBar percent={project.completion} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ============ SECTION 4: Charts — 2 donuts ============ */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        {projectDonutData.length > 0 && (
          <ChartCard title={translate("Projects by Status")} description={translate("project distribution across the organization")}>
            <StatusDonutChart data={projectDonutData} total={totalProjects} translate={translate} isHebrew={isHebrew} centerLabel={translate("projects")} />
          </ChartCard>
        )}
        {statusData.length > 0 && (
          <ChartCard title={translate("My Tasks by Status")} description={translate("Distribution of tasks assigned to me")}>
            <StatusDonutChart
              data={statusData}
              total={totalTasks}
              translate={translate}
              isHebrew={isHebrew}
              centerLabel={translate("Tasks")}
              onSliceClick={(entry, index) => {
                const slice = statusData[index]
                openDrillDown({ type: "status", statusId: slice?.id ?? 0, statusName: entry.title })
              }}
            />
          </ChartCard>
        )}
      </div>

      {/* ============ SECTION 5: Tasks Over Time ============ */}
      {timeSeriesData.length > 0 && (
        <ChartCard title={translate("Tasks Over Time")} description={translate("Created vs completed tasks per week (last 12 weeks)")}>
          <TaskActivityChart data={timeSeriesData} isHebrew={isHebrew} translate={translate} onPointClick={(date, metric) => openDrillDown({ type: "date", date, metric })} />
        </ChartCard>
      )}

      {/* ============ SECTION 6: Top Assignees ============ */}
      {assigneeData.length > 0 && (
        <ChartCard title={translate("Tasks by Assignee")} description={translate("Task workload distribution across team members")}>
          <AssigneesBarChart data={assigneeData} isHebrew={isHebrew} translate={translate} onBarClick={(entry) => openDrillDown({ type: "assignee", userName: entry.name })} />
        </ChartCard>
      )}

      {/* ============ SECTION 7: Employee Task Status Breakdown ============ */}
      {assigneeData.length > 0 && (
        <EmployeeBreakdownTable filteredTasks={filteredTasks} statuses={engine.statuses} translate={translate} isHebrew={isHebrew} />
      )}

      {/* ============ SECTION 8: Task Progress (FB-09) ============ */}
      <TaskProgressTable filteredTasks={filteredTasks} statuses={engine.statuses} projectsListRaw={projectsList} translate={translate} isHebrew={isHebrew} />

      {/* ============ DRILL-DOWN SHEET ============ */}
      <DrillDownSheet open={!!drillDown} onClose={closeDrillDown} title={getDrillDownTitle(drillDown, translate)} subtitle={getDrillDownSubtitle(drillDown, translate)} width='wide'>
        {drillDown?.type === "kpi" && (
          <KpiDrillDownContent tasks={projectsWithTasksRaw} category={drillDown.category} statuses={engine.statuses} translate={translate} projectsListRaw={projectsList} />
        )}
        {drillDown?.type === "status" && (
          <StatusDrillDownContent tasks={projectsWithTasksRaw} statusId={drillDown.statusId} statusName={drillDown.statusName} statuses={engine.statuses} translate={translate} projectsListRaw={projectsList} />
        )}
        {drillDown?.type === "project" && (
          <ProjectDrillDownContent tasks={projectsWithTasksRaw} projectId={drillDown.projectId} statuses={engine.statuses} translate={translate} />
        )}
        {drillDown?.type === "assignee" && (
          <AssigneeDrillDownContent tasks={projectsWithTasksRaw} userName={drillDown.userName} statuses={engine.statuses} translate={translate} projectsListRaw={projectsList} />
        )}
        {drillDown?.type === "overdue-projects" &&
          (() => {
            const now = new Date()
            const overdue = projectsList.filter((p: any) => {
              const deadline = p?.end_date || p?.deadline || p?.finish_to
              if (!deadline) return false
              const display = resolveEntityStatus(p, engine.statuses)
              return new Date(deadline) < now && display.category !== "completed"
            })
            const rows = overdue.map((p: any) => ({
              id: p.id,
              title: p.title || p.name || "-",
              status: resolveEntityStatus(p, engine.statuses).name,
              statusColor: resolveEntityStatus(p, engine.statuses).color,
              deadline: p.end_date || p.deadline || p.finish_to || "-",
              completion: (() => {
                const counts = taskCountsByProject.get(p.id)
                if (!counts || counts.total === 0) return "0%"
                return `${Math.round((counts.done / counts.total) * 100)}%`
              })(),
            }))
            return (
              <DrillDownTable
                columns={[
                  { header: translate("project name"), accessor: "title", sortable: true },
                  {
                    header: translate("status"), accessor: "status", sortable: true,
                    render: (v: string, row: any) => (
                      <span className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold' style={{ backgroundColor: row.statusColor + "20", color: row.statusColor }}>{v}</span>
                    ),
                  },
                  { header: translate("Due Date"), accessor: "deadline", sortable: true },
                  { header: translate("completion"), accessor: "completion", sortable: true },
                ]}
                data={rows}
              />
            )
          })()}
        {drillDown?.type === "at-risk-projects" &&
          (() => {
            const atRisk = projectTableData.filter((p) => p.totalTasks > 0 && p.completion < 30)
            const rows = atRisk.map((p) => ({
              id: p.id, title: p.title, status: p.statusDisplay.name, statusColor: p.statusDisplay.color,
              totalTasks: p.totalTasks, doneTasks: p.doneTasks, completion: `${p.completion}%`,
            }))
            return (
              <DrillDownTable
                columns={[
                  { header: translate("project name"), accessor: "title", sortable: true },
                  {
                    header: translate("status"), accessor: "status", sortable: true,
                    render: (v: string, row: any) => (
                      <span className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold' style={{ backgroundColor: row.statusColor + "20", color: row.statusColor }}>{v}</span>
                    ),
                  },
                  { header: translate("tasks total"), accessor: "totalTasks", sortable: true },
                  { header: translate("tasks done"), accessor: "doneTasks", sortable: true },
                  { header: translate("completion"), accessor: "completion", sortable: true },
                ]}
                data={rows}
              />
            )
          })()}
        {drillDown?.type === "date" && (
          <DateDrillDownContent timeSeriesRaw={timeSeriesRaw || []} date={drillDown.date} statuses={engine.statuses} translate={translate} />
        )}
      </DrillDownSheet>
    </div>
  )
})

export default TasksAnalytics
